import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { all_routes } from "../../../router/all_routes";
import {
  gender,
  districts as jkDistricts,
} from "../../../../core/common/selectoption/selectoption";
import CommonSelect from "../../../../core/common/commonSelect";
import { useStudent } from "../hooks/useStudent";
import { StudentPayloadType } from "../types/studentRequest";
import { useCourse } from "../../../configuration/course/hooks/useCourse";
import { showToast } from "../../../../core/utils/alert";
import { useActiveSession } from "../../../../utils/getActiveSession";
import { useBatchTiming } from "../../../configuration/batch-timing/hooks/useBatchTiming";
import { useCenter } from "../../centers/hooks/useCenter";
import StudentModals from "../studentModals";
import { useSelector } from "react-redux";
import { City, Country, State } from "country-state-city";
import {
  selectCurrentUser,
  selectUserRole,
} from "../../../../core/data/slice/authSlice";
import PhotoCaptureCropper from "../../../../core/common/photoCaptureCropper";
import StarterKitNoticeModal from "../quick-register/StarterKitNoticeModal";
import { canEditStudent } from "../utils/studentPermission";
import { API_CONFIG } from "../../../../core/config";
import { getApiErrorMessage } from "../../../../core/utils/errorHandler";

type LocationOption = { value: string; label: string; isoCode?: string };

const DATE_PICKER_POPUP_STYLE = { zIndex: 1060 };
const disableFutureDates = (current: dayjs.Dayjs) =>
  Boolean(current && current.isAfter(dayjs().endOf("day")));
const disableDobDates = (current: dayjs.Dayjs) => {
  if (!current) return false;
  const today = dayjs();
  const minDate = today.subtract(16, "year").startOf("day");
  const maxDate = today.subtract(3, "year").endOf("day");
  return current.isBefore(minDate) || current.isAfter(maxDate);
};
const disableDojDates = (current: dayjs.Dayjs, isSuperAdmin: boolean = false) => {
  if (!current) return false;
  // Future dates are always disabled
  if (current.isAfter(dayjs().endOf("day"))) return true;
  // For non-superadmins, DOJ cannot be older than 10 days in the past
  if (!isSuperAdmin) {
    const minDojDate = dayjs().subtract(10, "day").startOf("day");
    if (current.isBefore(minDojDate)) return true;
  }
  return false;
};

const classOptions = [
  { value: "UKG", label: "UKG" },
  { value: "1st", label: "1st" },
  { value: "2nd", label: "2nd" },
  { value: "3rd", label: "3rd" },
  { value: "4th", label: "4th" },
  { value: "5th", label: "5th" },
  { value: "6th", label: "6th" },
  { value: "7th", label: "7th" },
  { value: "8th", label: "8th" },
  { value: "9th", label: "9th" },
  { value: "10th", label: "10th" },
];

/**
 * AddStudent Component
 * Comprehensive form for adding/editing student information in the school management system.
 * Handles student personal details, family info, academic records, and medical history.
 */
const AddStudent = () => {
  // Get user role and info from Redux store
  const userRole = useSelector(selectUserRole);
  const currentUser = useSelector(selectCurrentUser);
  const isSuperAdmin = userRole === "superadmin";

  const activeSession = useActiveSession();
  const sessionId = activeSession?.id || 0;
  const sessionName = activeSession?.name;
  const routes = all_routes;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const {
    updateStudent,
    addNewStudent,
    uploadStudentPhoto,
    useGetStudentById,
    isUpdating,
    isAdding,
    addError,
    updateError,
  } = useStudent();
  const studentData = useGetStudentById(id || "");
  const { getCourseState } = useCourse({ published: true });
  const { courses } = getCourseState();

  const courseOptions = useMemo(
    () =>
      courses
        .filter((course) => course !== null)
        .map((course) => ({
          value: course.id.toString(),
          label: course.name,
          id: course.id,
        })),
    [courses]
  );

  // Add batch timings fetch
  const { batchTimings = [], isLoading: isBatchTimingsLoading } = useBatchTiming({ size: 1000 });

  // Always call the hook, but skip the API call if not a superadmin
  const { useCenterList } = useCenter({ skip: true });
  const { data: centerResponse } = useCenterList();
  const centers = isSuperAdmin ? (centerResponse?.data?.items || []) : [];

  // Convert centers to select options (only used by superadmin)
  const centerOptions = useMemo(
    () =>
      isSuperAdmin
        ? centers
          .filter((center) => center !== null)
          .map((center) => ({
            value: center.id.toString(),
            label: center.name,
          }))
        : [],
    [centers, isSuperAdmin]
  );

  /** State Management */
  // Controls if form is in edit mode
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const isEditAllowed = isEdit ? canEditStudent(userRole, studentData?.created_at, studentData?.registration_date) : true;
  // Default date for date picker fields
  const [defaultDate, setDefaultDate] = useState<dayjs.Dayjs | null>(null);
  // Tracks Aadhar verification status
  const [isAadharVerified, setIsAadharVerified] = useState<boolean>(false);
  // Controls visibility of OTP input field
  const [showOtpField, setShowOtpField] = useState<boolean>(false);

  // State for form fields
  const [formData, setFormData] = useState<StudentPayloadType>({
    name: "",
    profile: {
      gender: "",
      dob: "",
      fathers_name: "",
      fathers_occupation: "",
      email: "",
      phone: undefined,
      emergency_contact: undefined,
      education: "",
      institution_name: "",
      class: "",
      aadhar_number: undefined,
    },
    address: {
      address: "",
      pincode: undefined,
      district: "",
      state: "Jammu and Kashmir",
      country: "India",
    },
    status: "active",
    center_id: currentUser?.center_id ?? 0,
    course_id: 0,
    stage_id: 0,
    session_id: sessionId,
    batch_id: null,
    registration_date: dayjs().format("YYYY-MM-DD"),
  });

  const selectedCourse = useMemo(
    () => courses.find((course) => course && course.id === formData.course_id),
    [courses, formData.course_id]
  );
  const levelOptions = useMemo(
    () =>
      selectedCourse?.courseStages
        ?.filter((stage) => stage !== null)
        ?.map((stage) => ({
          value: stage.id.toString(),
          label: stage.name,
        })) || [],
    [selectedCourse]
  );

  const filteredBatchTimings = useMemo(
    () => {
      if (!formData.course_id) {
        return [];
      }

      return batchTimings.filter((batch) => {
        const courseMatch = batch.course_id === formData.course_id;
        const centerMatch = isSuperAdmin
          ? batch.center_id === formData.center_id
          : true;
        return courseMatch && centerMatch;
      });
    },
    [
      batchTimings,
      formData.center_id,
      formData.course_id,
      isSuperAdmin,
    ]
  );

  const batchTimingOptions = useMemo(
    () =>
      filteredBatchTimings
        .filter((batch) => batch !== null)
        .map((batch) => ({
          value: batch.id.toString(),
          label: `${batch.name} (${batch.start_time} - ${batch.end_time})`,
          description: `[${batch.days?.join(", ")}]`,
        })),
    [filteredBatchTimings]
  );

  // Add validation states for number fields
  const [formErrors, setFormErrors] = useState({
    phone: "",
    emergency_contact: "",
    pincode: "",
    aadhar_number: "",
    dob: "",
  });

  // Add state for DatePicker focused status
  const [isDatePickerFocused, setIsDatePickerFocused] = useState(false);
  const dobPickerValue = useMemo(
    () => (formData.profile.dob ? dayjs(formData.profile.dob) : null),
    [formData.profile.dob]
  );
  const dobPickerStyle = useMemo<React.CSSProperties>(
    () => ({
      backgroundImage: "none",
      boxShadow: isDatePickerFocused
        ? formErrors.dob
          ? "0 0 0 0.25rem rgba(220, 53, 69, 0.25)"
          : "none"
        : "none",
    }),
    [formErrors.dob, isDatePickerFocused]
  );

  // Photo upload state
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoError, setPhotoError] = useState("");

  // States for geographic data
  const [countries, setCountries] = useState<LocationOption[]>([]);
  const [states, setStates] = useState<LocationOption[]>([]);
  const [districts, setDistricts] = useState<LocationOption[]>([]);

  // Load countries from installed country-state-city package
  useEffect(() => {
    const countryOptions = Country.getAllCountries().map((country) => ({
      value: country.name,
      label: country.name,
      isoCode: country.isoCode,
    }));
    setCountries(countryOptions);
  }, []);

  // Load states when country changes
  useEffect(() => {
    if (!formData.address.country) {
      setStates([]);
      return;
    }

    const selectedCountry = Country.getAllCountries().find(
      (country) => country.name === formData.address.country
    );

    if (!selectedCountry) {
      setStates([]);
      return;
    }

    const stateOptions = State.getStatesOfCountry(selectedCountry.isoCode).map(
      (state) => ({
        value: state.name,
        label: state.name,
        isoCode: state.isoCode,
      })
    );
    setStates(stateOptions);
  }, [formData.address.country]);

  // Load districts when state changes
  useEffect(() => {
    if (!formData.address.state || !formData.address.country) {
      setDistricts([]);
      return;
    }

    if (formData.address.state === "Jammu and Kashmir") {
      setDistricts(jkDistricts);
      return;
    }

    const selectedCountry = Country.getAllCountries().find(
      (country) => country.name === formData.address.country
    );
    const selectedState = states.find(
      (state) => state.value === formData.address.state
    );

    if (!selectedCountry || !selectedState?.isoCode) {
      setDistricts([]);
      return;
    }

    const districtOptions = City.getCitiesOfState(
      selectedCountry.isoCode,
      selectedState.isoCode
    ).map((city) => ({
      value: city.name,
      label: city.name,
    }));
    setDistricts(districtOptions);
  }, [formData.address.state, formData.address.country, states]);

  // Add this validation function
  const validateAge = (date: string) => {
    if (!date) {
      setFormErrors((prev) => ({
        ...prev,
        dob: "",
      }));
      return true;
    }

    const birthDate = dayjs(date);
    const today = dayjs();

    // Calculate exact dates for 3 and 16 years age limit
    const minimumDate = today.subtract(16, "year").startOf("day");
    const maximumDate = today.subtract(3, "year").endOf("day");

    // Check if birthdate is outside the valid range
    const isTooOld = birthDate.isBefore(minimumDate);
    const isTooYoung = birthDate.isAfter(maximumDate);

    let errorMessage = "";
    if (isTooYoung) {
      errorMessage = `Student must be at least 3 years old (born on or before ${maximumDate.format(
        "DD-MM-YYYY"
      )})`;
    } else if (isTooOld) {
      errorMessage = `Student age cannot exceed 16 years (born on or after ${minimumDate.format(
        "DD-MM-YYYY"
      )})`;
    }

    // Update the form errors state
    setFormErrors((prev) => ({
      ...prev,
      dob: errorMessage,
    }));

    return errorMessage === "";
  };

  const validateDoj = (date: string): boolean => {
    if (!date || isSuperAdmin) return true;
    const dojDate = dayjs(date).startOf("day");
    const minDojDate = dayjs().subtract(10, "day").startOf("day");
    const today = dayjs().endOf("day");

    if (dojDate.isAfter(today)) {
      showToast({
        title: "Validation Error",
        text: "Date of Joining cannot be in the future.",
        icon: "warning",
      });
      return false;
    }
    if (dojDate.isBefore(minDojDate)) {
      showToast({
        title: "Validation Error",
        text: "Date of Joining cannot be older than 10 days in the past.",
        icon: "warning",
      });
      return false;
    }
    return true;
  };

  // Add function to validate number fields
  const validateNumberField = (value: string, field: string): boolean => {
    // Check if value is a valid number
    if (value === "" || value === undefined || value === null) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
      return true; // Allow empty values and clear error
    }

    const numberRegex = /^[0-9]+$/;
    const isValid = numberRegex.test(value);

    // Additional specific validations
    let errorMessage = "";

    if (!isValid) {
      // Use field-specific error messages
      switch (field) {
        case "phone":
          errorMessage = "Please enter a valid phone number.";
          break;
        case "emergency_contact":
          errorMessage = "Please enter a valid contact number.";
          break;
        case "aadhar_number":
          errorMessage = "Please enter a valid Aadhar number.";
          break;
        case "pincode":
          errorMessage = "Please enter a valid pincode.";
          break;
        default:
          errorMessage = "Please enter numbers only";
      }
    } else {
      // Field-specific validations
      switch (field) {
        case "phone":
        case "emergency_contact":
          if (value.length !== 10) {
            errorMessage = "Phone number must be exactly 10 digits";
          }
          break;
        case "aadhar_number":
          if (value.length !== 12) {
            errorMessage = "Aadhar number must be exactly 12 digits";
          }
          break;
        case "pincode":
          if (value.length !== 6) {
            errorMessage = "Pincode must be exactly 6 digits";
          }
          break;
      }
    }

    // Update error state
    setFormErrors((prev) => ({
      ...prev,
      [field]: errorMessage,
    }));

    return errorMessage === "";
  };

  /**
   * Initializes form based on route
   * - Sets default values when in edit mode
   * - Resets form when in add mode
   */
  useEffect(() => {
    if (studentData && id) {
      setIsEdit(true);
      setFormData((prev) => ({
        ...prev,
        session_id: sessionId,
        name: studentData.name,
        profile: {
          gender: studentData.profile.gender?.toLowerCase() || "",
          dob: studentData.profile.dob,
          fathers_name: studentData.profile.fathers_name,
          fathers_occupation: studentData.profile.fathers_occupation,
          email: studentData.profile.email,
          phone: studentData.profile.phone || undefined,
          emergency_contact: studentData.profile.emergency_contact || undefined,
          education: studentData.profile.education,
          institution_name: studentData.profile.institution_name,
          class: studentData.profile.class,
          aadhar_number: studentData.profile.aadhar_number || undefined,
        },
        address: {
          address: studentData.address?.address || "",
          pincode: studentData.address?.pincode || undefined,
          district: studentData.address?.district || "",
          state: studentData.address?.state || "Jammu and Kashmir",
          country: studentData.address?.country || "India",
        },
        status: studentData.status,
        center_id: isSuperAdmin
          ? studentData.center?.id
          : currentUser?.center_id ?? 0, // Use nullish coalescing
        course_id: studentData.course?.id,
        stage_id: studentData.course?.stage?.id,
        batch_id: studentData.batch?.id || null,
        isImported: studentData.isImported,
        registration_date: studentData.registration_date || dayjs().format("YYYY-MM-DD"),
      }));
    } else {
      setIsEdit(false);
      setDefaultDate(null);
      setFormData((prev) => ({
        ...prev,
        session_id: sessionId,
        center_id: isSuperAdmin ? 0 : currentUser?.center_id ?? 0, // Use nullish coalescing
        registration_date: dayjs().format("YYYY-MM-DD"),
        address: {
          ...prev.address,
          state: prev.address.state || "Jammu and Kashmir",
          country: prev.address.country || "India",
        },
      }));
    }
  }, [studentData, id, sessionId, isSuperAdmin, currentUser?.center_id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEditAllowed) {
      showToast({
        title: "Permission Denied",
        text: "Editing is restricted after 1 week of student creation.",
        icon: "error",
      });
      return;
    }
    if (!formData.profile.fathers_name?.trim()) {
      showToast({
        title: "Validation Error",
        text: "Father's name is required.",
        icon: "warning",
      });
      return;
    }
    if (!formData.profile.fathers_occupation?.trim()) {
      showToast({
        title: "Validation Error",
        text: "Father's occupation is required.",
        icon: "warning",
      });
      return;
    }
    if (!formData.profile.dob) {
      showToast({
        title: "Validation Error",
        text: "Date of Birth is required.",
        icon: "warning",
      });
      return;
    }
    if (!formData.profile.institution_name?.trim()) {
      showToast({
        title: "Validation Error",
        text: "School/College name is required.",
        icon: "warning",
      });
      return;
    }
    if (!formData.address.address?.trim()) {
      showToast({
        title: "Validation Error",
        text: "Address is required.",
        icon: "warning",
      });
      return;
    }
    if (!formData.address.pincode) {
      showToast({
        title: "Validation Error",
        text: "Pin code is required.",
        icon: "warning",
      });
      return;
    }
    if (formData.profile.dob && !validateAge(formData.profile.dob)) {
      showToast({
        title: "Validation Error",
        text: "Please correct the Date of Birth. Age must be between 3 and 16 years.",
        icon: "warning",
      });
      return;
    }
    if (formData.registration_date && !validateDoj(formData.registration_date)) {
      return;
    }
    const finalFormData = {
      ...formData,
      profile: {
        ...formData.profile,
        email: formData.profile.email?.trim() || undefined,
        emergency_contact: formData.profile.emergency_contact || undefined,
      }
    };
    const response = await updateStudent(id!, finalFormData);
    // console.log(response, "response");
    if (response.data) {
      if (photoFile) {
        const photoResponse = await uploadStudentPhoto(id!, photoFile);
        if (!photoResponse.success) {
          showToast({
            title: "Warning",
            text:
              "Student updated but photo upload failed: " +
              (photoResponse.error || ""),
            icon: "warning",
          });
        }
      }

      showToast({
        title: "Success",
        text: "Student updated successfully",
        icon: "success",
      });
      navigate(routes.studentList);
    } else {
      showToast({
        title: "Error",
        text: response.error || "Student update failed",
        icon: "error",
      });
    }
  };

  // State to control the post-add document modal
  const [showPostAddModal, setShowPostAddModal] = useState(false);
  const [newlyCreatedStudentId, setNewlyCreatedStudentId] = useState<string | null>(null);
  const [showKitNoticeModal, setShowKitNoticeModal] = useState(false);
  const [kitNoticeMessage, setKitNoticeMessage] = useState("");
  const [registeredStudentName, setRegisteredStudentName] = useState("");
  const [registeredCourseName, setRegisteredCourseName] = useState("");

  const handleAddSubmit = async () => {
    if (!formData.profile.fathers_name?.trim()) {
      showToast({
        title: "Validation Error",
        text: "Father's name is required.",
        icon: "warning",
      });
      return;
    }
    if (!formData.profile.fathers_occupation?.trim()) {
      showToast({
        title: "Validation Error",
        text: "Father's occupation is required.",
        icon: "warning",
      });
      return;
    }
    if (!formData.profile.dob) {
      showToast({
        title: "Validation Error",
        text: "Date of Birth is required.",
        icon: "warning",
      });
      return;
    }
    if (!formData.profile.institution_name?.trim()) {
      showToast({
        title: "Validation Error",
        text: "School/College name is required.",
        icon: "warning",
      });
      return;
    }
    if (!formData.address.address?.trim()) {
      showToast({
        title: "Validation Error",
        text: "Address is required.",
        icon: "warning",
      });
      return;
    }
    if (!formData.address.pincode) {
      showToast({
        title: "Validation Error",
        text: "Pin code is required.",
        icon: "warning",
      });
      return;
    }
    if (formData.profile.dob && !validateAge(formData.profile.dob)) {
      showToast({
        title: "Validation Error",
        text: "Please correct the Date of Birth. Age must be between 3 and 16 years.",
        icon: "warning",
      });
      return;
    }
    if (formData.registration_date && !validateDoj(formData.registration_date)) {
      return;
    }
    // Ensure student is created as active
    const payloadWithActiveStatus = {
      ...formData,
      status: "active",
      profile: {
        ...formData.profile,
        email: formData.profile.email?.trim() || undefined,
        emergency_contact: formData.profile.emergency_contact || undefined,
      }
    };

    // Step 1: Create the student
    const response = await addNewStudent(payloadWithActiveStatus);

    if (!response.data?.student) {
      showToast({
        title: "Error",
        text: response.error || "Student creation failed",
        icon: "error",
      });
      return;
    }

    const newStudentId = response.data.student.id.toString();

    // Step 2: Upload the photo
    if (photoFile) {
      const photoResponse = await uploadStudentPhoto(newStudentId, photoFile);
      if (!photoResponse.success) {
        showToast({
          title: "Warning",
          text:
            "Student created but photo upload failed: " +
            (photoResponse.error || ""),
          icon: "warning",
        });
      }
    }

    const starterKit = (response.data as any)?.starter_kit;
    const kitNotAssigned = starterKit && starterKit.assigned === false;
    const selectedCourseObj = courses.find((c: any) => c && c.id === formData.course_id);

    setNewlyCreatedStudentId(newStudentId);
    setRegisteredStudentName(formData.name || "");
    setRegisteredCourseName(selectedCourseObj?.name || "");

    if (kitNotAssigned) {
      setKitNoticeMessage(starterKit.message || "Starter kit stock is empty for this course.");
      setShowKitNoticeModal(true);
    } else {
      showToast({
        title: "Success",
        text: starterKit?.assigned
          ? "Student created and starter kit assigned successfully!"
          : "Student created successfully!",
        icon: "success",
      });
      setShowPostAddModal(true);
    }
  };

  // Capitalize first letter of each word
  const capitalizeWords = (str: string): string => {
    return str.replace(/(^|\s)\S/g, (char) => char.toUpperCase());
  };

  // Update form data handler
  const handleInputChange = (path: string, value: string | number | undefined) => {
    if (path === "profile.dob") {
      validateAge(value as string);
    }

    // Auto-capitalize first letter of each word for personal details & institution & address
    if (
      typeof value === "string" &&
      [
        "name",
        "profile.fathers_name",
        "profile.fathers_occupation",
        "profile.institution_name",
        "address.address",
      ].includes(path)
    ) {
      value = capitalizeWords(value);
    }

    // Handle number fields validation
    if (
      [
        "profile.phone",
        "profile.emergency_contact",
        "profile.aadhar_number",
        "address.pincode",
      ].includes(path)
    ) {
      const fieldName = path.split(".")[1];
      const isValid = validateNumberField(value?.toString() || "", fieldName);

      // If valid, convert to number or undefined if empty
      if (isValid) {
        value = (value === "" || value === undefined || value === null) ? undefined : Number(value);
      }
    }

    setFormData((prev) => {
      const keys = path.split(".");
      if (keys.length === 1) {
        return { ...prev, [keys[0]]: value };
      }
      if (keys.length === 2) {
        const [parent, child] = keys;
        return {
          ...prev,
          [parent]: {
            ...(prev[parent as keyof StudentPayloadType] as object),
            [child]: value,
          },
        };
      }
      return prev;
    });
  };

  const handleCenterChange = (
    value: { value: string; label: string } | null
  ) => {
    const centerId = Number(value?.value) || 0;

    setFormData((prev) => ({
      ...prev,
      center_id: centerId,
      batch_id: null,
      profile: {
        ...prev.profile,
        batch_timing: "",
      },
    }));

  };

  // Update handleBatchTimingChange to use the formatted label
  const handleBatchTimingChange = (
    value: { value: string; label: string } | null
  ) => {
    const batchId = value ? Number(value.value) : null;
    const batchName = value?.label || "";

    setFormData((prev) => ({
      ...prev,
      batch_id: batchId,
      profile: {
        ...prev.profile,
        batch_timing: batchName,
      },
    }));
  };

  const handleCourseChange = (value: { value: string } | null) => {
    const courseId = Number(value?.value) || 0;

    const selectedCrs = courses.find((course) => course && course.id === courseId);
    const validStages = selectedCrs?.courseStages?.filter((stage) => stage !== null) || [];
    const firstStage = validStages[0];
    const defaultStageId = firstStage ? firstStage.id : 0;

    setFormData((prev) => ({
      ...prev,
      course_id: courseId,
      stage_id: defaultStageId,
      batch_id: null,
      profile: {
        ...prev.profile,
        batch_timing: "",
      },
    }));

  };

  const handleLevelChange = (value: { value: string } | null) => {
    const stageId = Number(value?.value) || 0;

    setFormData((prev) => ({
      ...prev,
      stage_id: stageId,
    }));
  };

  // Function to check if all required fields are filled
  const isFormValid = () => {
    const { name, profile, address, center_id, course_id, stage_id } =
      formData;

    // Check if there are any validation errors
    const hasValidationErrors = Object.values(formErrors).some(
      (error) => error !== ""
    );

    if (isEdit && !isEditAllowed) {
      return false;
    }

    return (
      name?.trim() !== "" &&
      profile.fathers_name?.trim() !== "" &&
      profile.fathers_occupation?.trim() !== "" &&
      Boolean(profile.dob) &&
      profile.gender !== "" &&
      profile.phone !== undefined &&
      profile.phone !== 0 &&
      profile.institution_name?.trim() !== "" &&
      profile.class?.trim() !== "" &&
      address.country !== "" &&
      address.state !== "" &&
      address.district !== "" &&
      address.address?.trim() !== "" &&
      address.pincode !== undefined &&
      address.pincode !== 0 &&
      center_id !== 0 &&
      course_id !== 0 &&
      stage_id !== 0 &&
      !hasValidationErrors &&
      (isEdit || photoFile !== null)
    );
  };

  // console.log(formData, "--formData");
  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content content-two">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="mb-1">{isEdit ? "Edit Student" : "Full Registration"}</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.studentList}>Students</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEdit ? "Edit Student" : "Full Registration"}
                  </li>
                </ol>
              </nav>
              {/* Display Active Session Name */}
              {sessionName && (
                <div className="alert alert-info mt-2 p-3 rounded-3 shadow-sm border-0">
                  <strong className="text-dark">Session:</strong>{" "}
                  <span className="text-primary fw-bold">{sessionName}</span>
                </div>
              )}
              {isEdit && !isEditAllowed && (
                <div className="alert alert-danger mt-2 p-3 rounded-3 shadow-sm border-0 d-flex align-items-center">
                  <i className="ti ti-alert-triangle fs-4 me-2"></i>
                  <div>
                    <strong>Editing Locked:</strong> Center Admins can only edit student details within 1 week of student creation. Please contact a Super Admin to make changes.
                  </div>
                </div>
              )}
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
              {!isEdit && (
                <Link
                  to={routes.quickRegister}
                  className="btn btn-outline-primary d-flex align-items-center mb-2"
                >
                  <i className="ti ti-bolt me-2" />
                  Quick Register
                </Link>
              )}
              <Link
                to={routes.studentList}
                className="btn btn-primary d-flex align-items-center mb-2"
              >
                <i className="ti ti-list me-2" />
                Student List
              </Link>
            </div>
          </div>
          {/* /Page Header */}
          <div className="row">
            <div className="col-md-12">
              <form>
                {/* Aadhar Authentication Section 
                    Handles verification of student's Aadhar number through OTP */}
                <div className="card mb-3">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-id fs-16" />
                      </span>
                      <h4 className="text-dark">Aadhar Authentication</h4>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row align-items-center">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Aadhar Number
                          </label>
                          <input
                            type="text"
                            className={`form-control ${formErrors.aadhar_number ? "is-invalid" : ""
                              }`}
                            placeholder="Enter 12-digit Aadhar number"
                            maxLength={12}
                            value={
                              formData.profile.aadhar_number === undefined || formData.profile.aadhar_number === 0
                                ? ""
                                : formData.profile.aadhar_number
                            }
                            onChange={(e) =>
                              handleInputChange(
                                "profile.aadhar_number",
                                e.target.value
                              )
                            }
                          />
                          {formErrors.aadhar_number && (
                            <div className="invalid-feedback">
                              {formErrors.aadhar_number}
                            </div>
                          )}
                        </div>
                      </div>
                      {!isEdit && (
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">&nbsp;</label>
                            <div>
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => setShowOtpField(true)}
                              >
                                Verify Aadhar
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      {showOtpField && (
                        <>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Enter OTP</label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Enter 6-digit OTP"
                                maxLength={6}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">&nbsp;</label>
                              <div>
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  onClick={() => setIsAadharVerified(true)}
                                >
                                  Verify OTP
                                </button>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {/* Personal Information Section
                    Essential student details including:
                    - Student photo upload (4MB max, JPG/PNG/SVG)
                    - Full name
                    - Father's name
                    - Gender
                    - Date of birth */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-info-square-rounded fs-16" />
                      </span>
                      <h4 className="text-dark">Personal Information</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    {/* ── Photo Upload / Capture / Crop ── */}
                    <div className="row mb-4">
                      <div className="col-12">
                        <PhotoCaptureCropper
                          photo={photoFile}
                          existingPhotoUrl={
                            studentData?.docs?.photo
                              ? `${API_CONFIG.BASE_URL}students/get-thumbnail/${studentData.docs.photo}?v=${Date.now()}`
                              : null
                          }
                          onPhotoChange={(file) => {
                            setPhotoFile(file);
                            if (file) setPhotoError("");
                          }}
                          error={photoError}
                          required={!isEdit}
                        />
                      </div>
                    </div>

                    {/* Personal Details Form Fields */}
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter name"
                            style={{ textTransform: "capitalize" }}
                            value={formData.name ?? ""}
                            onChange={(e) =>
                              handleInputChange("name", e.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Father's Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Father's name"
                            style={{ textTransform: "capitalize" }}
                            value={formData.profile.fathers_name ?? ""}
                            onChange={(e) =>
                              handleInputChange(
                                "profile.fathers_name",
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Father's Occupation <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Father's Occupation"
                            style={{ textTransform: "capitalize" }}
                            value={formData.profile.fathers_occupation ?? ""}
                            onChange={(e) =>
                              handleInputChange(
                                "profile.fathers_occupation",
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Gender <span className="text-danger">*</span>
                          </label>
                          <CommonSelect
                            className="select"
                            options={gender}
                            value={
                              gender.find(
                                (option) =>
                                  option.value?.toLowerCase() === formData.profile?.gender?.toLowerCase()
                              ) || undefined
                            }
                            onChange={(value) =>
                              handleInputChange(
                                "profile.gender",
                                value?.value || ""
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Date of Birth <span className="text-danger">*</span>
                          </label>
                          <div className="position-relative">
                            <div className="input-icon position-relative">
                              <DatePicker
                                className={`form-control datetimepicker ${formErrors.dob ? "is-invalid" : ""
                                  }`}
                                style={dobPickerStyle}
                                format="DD-MM-YYYY"
                                value={dobPickerValue}
                                onChange={(date) => {
                                  const formattedDate =
                                    date?.format("YYYY-MM-DD") || "";
                                  handleInputChange(
                                    "profile.dob",
                                    formattedDate
                                  );
                                }}
                                onFocus={() => setIsDatePickerFocused(true)}
                                onBlur={() => setIsDatePickerFocused(false)}
                                placeholder="Select Date"
                                popupStyle={DATE_PICKER_POPUP_STYLE}
                                disabledDate={disableDobDates}
                              />
                              <span className="input-icon-addon">
                                <i
                                  className={`ti ti-calendar ${formErrors.dob ? "text-danger" : ""
                                    }`}
                                />
                              </span>
                            </div>
                            {formErrors.dob && (
                              <div className="invalid-feedback d-block">
                                {formErrors.dob}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Date of Join */}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Date of Join <span className="text-muted">(Optional)</span>
                          </label>
                          <div className="position-relative">
                            <div className="input-icon position-relative">
                              <DatePicker
                                className="form-control datetimepicker"
                                style={{ backgroundImage: "none" }}
                                format="DD-MM-YYYY"
                                value={formData.registration_date ? dayjs(formData.registration_date) : null}
                                onChange={(date) => {
                                  const formattedDate = date?.format("YYYY-MM-DD") || "";
                                  handleInputChange("registration_date", formattedDate);
                                }}
                                placeholder="Select Date"
                                popupStyle={DATE_PICKER_POPUP_STYLE}
                                disabledDate={(current) => disableDojDates(current, isSuperAdmin)}
                              />
                              <span className="input-icon-addon">
                                <i className="ti ti-calendar" />
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Center & Course Details Section */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-book fs-16" />
                      </span>
                      <h4 className="text-dark">
                        {isSuperAdmin
                          ? "Center & Course Details"
                          : "Course Details"}
                      </h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      {isSuperAdmin && (
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Center <span className="text-danger">*</span>
                            </label>
                            <CommonSelect
                              options={centerOptions}
                              value={centerOptions.find(
                                (option) =>
                                  option.value === formData.center_id.toString()
                              )}
                              onChange={handleCenterChange}
                              placeholder="Select Center"
                            />
                          </div>
                        </div>
                      )}
                      <div className={`col-md-${isSuperAdmin ? "6" : "6"}`}>
                        <div className="mb-3">
                          <label className="form-label">
                            Select Course <span className="text-danger">*</span>
                          </label>
                          <CommonSelect
                            placeholder="Select Course"
                            className="select"
                            options={courseOptions}
                            value={courseOptions.find(
                              (option) =>
                                option.value === formData?.course_id?.toString()
                            )}
                            onChange={handleCourseChange}
                            isDisabled={isEdit}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-6 d-none">
                        <div className="mb-3">
                          <label className="form-label">
                            Level <span className="text-danger">*</span>
                          </label>
                          <CommonSelect
                            placeholder="Select Level"
                            className="select"
                            options={levelOptions}
                            value={levelOptions.find(
                              (option) =>
                                option.value === formData.stage_id?.toString()
                            )}
                            onChange={handleLevelChange}
                            isDisabled={(isEdit && !formData.isImported) || !formData.course_id}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Preferred Batch Timing
                          </label>
                          <CommonSelect
                            className="select"
                            options={batchTimingOptions}
                            value={batchTimingOptions.find(
                              (option) =>
                                option.value === formData.batch_id?.toString()
                            ) || null}
                            onChange={handleBatchTimingChange}
                            isDisabled={!formData.course_id}
                            placeholder="Select Batch Timing"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Contact Details Section */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-phone fs-16" />
                      </span>
                      <h4 className="text-dark">Contact Details</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Email Address
                          </label>
                          <input
                            type="email"
                            className="form-control"
                            placeholder="Enter email address"
                            value={formData.profile.email ?? ""}
                            onChange={(e) =>
                              handleInputChange("profile.email", e.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Mobile Number <span className="text-danger">*</span>
                          </label>
                          <input
                            type="tel"
                            className={`form-control ${formErrors.phone ? "is-invalid" : ""
                              }`}
                            placeholder="Enter mobile number"
                            maxLength={10}
                            value={
                              formData.profile.phone === undefined || formData.profile.phone === 0
                                ? ""
                                : formData.profile.phone
                            }
                            onChange={(e) =>
                              handleInputChange("profile.phone", e.target.value)
                            }
                          />
                          {formErrors.phone && (
                            <div className="invalid-feedback">
                              {formErrors.phone}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Emergency Contact Number
                          </label>
                          <input
                            type="tel"
                            className={`form-control ${formErrors.emergency_contact ? "is-invalid" : ""
                              }`}
                            placeholder="Enter emergency contact number"
                            maxLength={10}
                            value={
                              formData.profile.emergency_contact === undefined || formData.profile.emergency_contact === 0
                                ? ""
                                : formData.profile.emergency_contact
                            }
                            onChange={(e) =>
                              handleInputChange(
                                "profile.emergency_contact",
                                e.target.value
                              )
                            }
                          />
                          {formErrors.emergency_contact && (
                            <div className="invalid-feedback">
                              {formErrors.emergency_contact}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Education Details Section */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-book fs-16" />
                      </span>
                      <h4 className="text-dark">Education Details</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            School/College Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter institution name"
                            style={{ textTransform: "capitalize" }}
                            value={formData.profile.institution_name ?? ""}
                            onChange={(e) =>
                              handleInputChange(
                                "profile.institution_name",
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Class <span className="text-danger">*</span>
                          </label>
                          <CommonSelect
                            className="select"
                            options={classOptions}
                            value={classOptions.find(
                              (option) =>
                                option.value === formData.profile.class
                            )}
                            onChange={(value: any) =>
                              handleInputChange("profile.class", value?.value || "")
                            }
                            placeholder="Select Class"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address Section */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-map fs-16" />
                      </span>
                      <h4 className="text-dark">Address</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Country <span className="text-danger">*</span>
                          </label>
                          <CommonSelect
                            className="select"
                            options={countries}
                            value={countries.find(
                              (option) =>
                                option.value === formData.address.country
                            )}
                            onChange={(value) => {
                              handleInputChange(
                                "address.country",
                                value?.value || ""
                              );
                              handleInputChange("address.state", "");
                              handleInputChange("address.district", "");
                            }}
                            placeholder="Select Country"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            State <span className="text-danger">*</span>
                          </label>
                          <CommonSelect
                            className="select"
                            options={states}
                            value={states.find(
                              (option) =>
                                option.value === formData.address.state
                            )}
                            onChange={(value) => {
                              handleInputChange(
                                "address.state",
                                value?.value || ""
                              );
                              handleInputChange("address.district", "");
                            }}
                            isDisabled={!formData.address.country}
                            placeholder="Select State"
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            District <span className="text-danger">*</span>
                          </label>
                          <CommonSelect
                            className="select"
                            options={districts}
                            value={districts.find(
                              (option) => option.value === formData.address.district
                            )}
                            onChange={(value) =>
                              handleInputChange(
                                "address.district",
                                value?.value || ""
                              )
                            }
                            isDisabled={!formData.address.state}
                            placeholder="Select District"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Address <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter address"
                            style={{ textTransform: "capitalize" }}
                            value={formData.address.address ?? ""}
                            onChange={(e) =>
                              handleInputChange(
                                "address.address",
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Pin Code <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${formErrors.pincode ? "is-invalid" : ""
                              }`}
                            placeholder="Enter pin code"
                            maxLength={6}
                            value={
                              formData.address.pincode === undefined || formData.address.pincode === 0
                                ? ""
                                : formData.address.pincode
                            }
                            onChange={(e) =>
                              handleInputChange(
                                "address.pincode",
                                e.target.value
                              )
                            }
                          />
                          {formErrors.pincode && (
                            <div className="invalid-feedback">
                              {formErrors.pincode}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* /Other Details */}
                <div className="text-end">
                  <Link to={routes.studentList} className="btn btn-light me-3">
                    Cancel
                  </Link>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={isUpdating || isAdding || !isFormValid()}
                    onClick={isEdit ? handleSubmit : handleAddSubmit}
                  >
                    {isAdding
                      ? "Saving..."
                      : isUpdating
                        ? "Updating..."
                        : isEdit
                          ? "Update Student"
                          : "Add Student"}
                  </button>
                  {/* {addError && (
                    <div className="alert alert-danger mt-3">
                      Error: {getApiErrorMessage(addError)}
                    </div>
                  )}
                  {updateError && (
                    <div className="alert alert-danger mt-3">
                      Error: {getApiErrorMessage(updateError)}
                    </div>
                  )} */}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <StudentModals
        studentData={formData}
        StudentId={newlyCreatedStudentId || id}
      />

      <StarterKitNoticeModal
        show={showKitNoticeModal}
        studentName={registeredStudentName}
        courseName={registeredCourseName}
        message={kitNoticeMessage}
        onProceed={() => {
          setShowKitNoticeModal(false);
          setShowPostAddModal(true);
        }}
        onGoToList={() => {
          setShowKitNoticeModal(false);
          navigate(routes.studentList);
        }}
      />

      {/* Post-Add Document Upload Modal handled via React State */}
      {showPostAddModal && (
        <>
          <div className="modal fade show d-block" tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body text-center">
                  <span className="upload-icon">
                    <i className="ti ti-file-upload" style={{ fontSize: "40px", color: "#3D5EE1" }} />
                  </span>
                  <h4 className="mt-3">Upload Documents</h4>
                  <p>Would you like to upload documents for this student now?</p>
                  <div className="d-flex justify-content-center mt-4">
                    <button
                      type="button"
                      className="btn btn-light me-3"
                      onClick={() => {
                        setShowPostAddModal(false);
                        navigate(routes.studentList);
                      }}
                    >
                      Skip for Now
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        setShowPostAddModal(false);
                        if (newlyCreatedStudentId) {
                          navigate(routes.studentDocuments.replace(":id", newlyCreatedStudentId));
                        } else {
                          navigate(routes.studentList);
                        }
                      }}
                    >
                      Yes, Upload
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </>
  );
};

export default AddStudent;
